import { ComponentProps, MouseEvent, useRef, useState } from 'react'
import { alpha3ToNumeric } from 'i18n-iso-countries'
import { ComposableMap, Geographies, Geography } from 'react-simple-maps'
import styled from 'styled-components'
import countries from 'world-atlas/countries-110m.json'

import { ChartTooltipBody } from '@percent/lemonade'

import { RowItem, valueAmount } from '../../types/insights.types'
import { valueFormatterFor } from '../../utils/formatValue'
import {
  bucketColour,
  MAP_NO_DATA_COLOUR,
  rowsBuckets,
} from '../../utils/mapBuckets'
import { ChartBodyProps } from './chartBodies'

const ANTARCTICA_ID = '010'

// Gap between the cursor and the tooltip's bottom edge.
const TOOLTIP_OFFSET_PX = 12

// react-simple-maps reads a TopoJSON topology at runtime but types `geography` as GeoJSON only.
const WORLD = countries as unknown as ComponentProps<
  typeof Geographies
>['geography']

const MapContainer = styled.div`
  position: relative;
  width: 100%;
`

const WorldMap = styled(ComposableMap)`
  width: 100%;
  height: auto;

  // Countries are hover targets, not focusable controls: drop the browser's
  // focus/click outline on the SVG paths.
  path {
    outline: none;
  }
`

// Sits centred above the cursor and ignores the pointer, so moving onto the
// tooltip never steals the hover from the country underneath.
const TooltipAnchor = styled.div`
  position: absolute;
  pointer-events: none;
  transform: translate(-50%, calc(-100% - ${TOOLTIP_OFFSET_PX}px));
  z-index: 1;
`

type HoveredCountry = { row: RowItem; colour: string; x: number; y: number }

export type MapChartBodyProps = ChartBodyProps & {
  /** Names the value in the hover tooltip, e.g. "Total granted". */
  valueLabel?: string
}

export function MapChartBody({
  data,
  locale,
  valueLabel,
}: Readonly<MapChartBodyProps>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [hovered, setHovered] = useState<HoveredCountry>()

  if (data.type !== 'rows') return null

  const format = valueFormatterFor(data.rows[0]?.value, locale)
  const buckets = rowsBuckets(data)
  const rowsById = new Map(
    data.rows.map((row) => [alpha3ToNumeric(row.key), row])
  )

  const trackHover =
    (row: RowItem, colour: string) => (event: MouseEvent<SVGPathElement>) => {
      const rect = containerRef.current?.getBoundingClientRect()

      if (!rect) return

      setHovered({
        row,
        colour,
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      })
    }

  return (
    <MapContainer ref={containerRef}>
      <WorldMap
        width={800}
        height={360}
        projectionConfig={{ scale: 150, center: [0, 8] }}
      >
        <Geographies
          geography={WORLD}
          parseGeographies={(features) =>
            features.filter((feature) => feature.id !== ANTARCTICA_ID)
          }
        >
          {({ geographies }) =>
            geographies.map((geography) => {
              const row = rowsById.get(String(geography.id))
              const fill = row
                ? bucketColour(buckets, valueAmount(row.value))
                : MAP_NO_DATA_COLOUR
              const onHover = row ? trackHover(row, fill) : undefined

              return (
                <Geography
                  key={geography.rsmKey}
                  geography={geography}
                  fill={fill}
                  stroke="white"
                  strokeWidth={0.5}
                  tabIndex={-1}
                  aria-label={
                    row
                      ? `${row.label}: ${format(valueAmount(row.value))}`
                      : geography.properties?.name
                  }
                  onMouseEnter={onHover}
                  onMouseMove={onHover}
                  onMouseLeave={() => setHovered(undefined)}
                />
              )
            })
          }
        </Geographies>
      </WorldMap>
      {hovered && (
        <TooltipAnchor
          style={{ left: hovered.x, top: hovered.y }}
          data-testid="map-tooltip"
        >
          <ChartTooltipBody
            active
            label={hovered.row.label}
            payload={[
              {
                dataKey: 'value',
                name: valueLabel ?? '',
                value: valueAmount(hovered.row.value),
                color: hovered.colour,
              },
            ]}
            formatter={format}
          />
        </TooltipAnchor>
      )}
    </MapContainer>
  )
}
